import './filtertypes/filter-text.js';
import './filtertypes/filter-dropdown.js';
import './filtertypes/filter-language.js';
import './filtertypes/filter-boolean.js';

/**
 * Custom element for matrix-based search functionality.
 * Provides a search interface with autocomplete suggestions and filter management.
 * @class
 * @extends {HTMLElement}
 */
class MatrixSearch extends HTMLElement {
    /**
     * Initialize the MatrixSearch component.
     * Sets up initial state, flags, and configuration.
     * @constructor
     */
    constructor() {
        super();
        /** @type {boolean} Whether to use flag filtering */
        this.useFlags = this.getAttribute('useFlags') === 'true';
        /** @type {number} Current flag value for filtering */
        this.flag = this.useFlags ? 3 : 0;
        // No shadow DOM: this.attachShadow({ mode: 'open' });
        /** @type {Map<string, Object>} Map of selected filters by name */
        this.selectedFilters = new Map(); // To store selected filters by name
        /** @type {number|null} Timer ID for debouncing input */
        this.debounceTimer = null;
        /** @type {string} API endpoint for fetching suggestions */
        this.endpointUrl = this.getAttribute('endpoint') || '/search/features';
        /** @type {number} Index of highlighted suggestion (-1 means none) */
        this.highlightedSuggestionIndex = -1; // -1 means nothing selected

        // Parse initial filter state if provided
        this.initialState = null;
        const initialStateAttr = this.getAttribute('initial-state');
        if (initialStateAttr) {
            try {
                this.initialState = JSON.parse(initialStateAttr);
                // Set flag from initial state if available
                if (this.initialState && this.initialState.flag !== undefined) {
                    this.flag = parseInt(this.initialState.flag, 10);
                }
            } catch (e) {
                console.error('Error parsing initial-state attribute:', e);
            }
        }

        // Parse initial filter configuration if provided
        this.initialConfig = null;
        const initialConfigAttr = this.getAttribute('initial-config');
        if (initialConfigAttr) {
            try {
                this.initialConfig = JSON.parse(initialConfigAttr);
            } catch (e) {
                console.error('Error parsing initial-config attribute:', e);
            }
        }

        // Append directly to the component element
        this.innerHTML = `
            <div>
                <ul id="selected-list"></ul>                
                ${this.useFlags ? this.createFlagSelector() : ''}
                <input type="text" id="search-input" placeholder="Search filters...">
                <div id="autocomplete-suggestions"></div>
            </div>
        `;

        // Query elements within the component's light DOM using querySelector
        this.searchInput = this.querySelector('#search-input');
        if (this.useFlags) {
            const radios = this.querySelectorAll('input[name="softwareType"]');
            radios.forEach(radio => {
                radio.addEventListener('change', () => {
                    this.flag = parseInt(radio.value, 10);
                    this.collectFilterValues();
                });
            });
        }
        this.suggestionsContainer = this.querySelector('#autocomplete-suggestions');
        this.suggestionsContainer.style.display = 'none';
        this.selectedList = this.querySelector('#selected-list');
        this.filterChangeDebounceTimer = null; // Debounce timer for filter input changes
        this.currentFilterValues = {}; // Initialize class member to store current filter values
    }

    /**
     * Creates the HTML for the flag selector.
     * @returns {string} HTML string for the flag selector
     */
    createFlagSelector() {
        return `<div class="flag-selector">
            Software-Type: 
            <label><input type="radio" class="md" name="softwareType" value="3" ${this.flag === 3 ? 'checked' : ''}> All</label> 
            <label><input type="radio" class="md" name="softwareType" value="1" ${this.flag === 1 ? 'checked' : ''}> Downloadable</label> 
            <label><input type="radio" class="md" name="softwareType" value="2" ${this.flag === 2 ? 'checked' : ''}> Hosted (SaaS)</label>
        </div>`;
    }

    /**
     * Called when the element is inserted into the DOM.
     * Sets up event listeners and initializes from attributes.
     */
    connectedCallback() {
        this.searchInput.addEventListener('input', this.handleInput.bind(this));
        this.searchInput.addEventListener('keydown', this.handleKeyDown.bind(this)); // Add keydown listener
        this.searchInput.addEventListener('blur', this.handleBlur.bind(this)); // Hide suggestions on blur
        this.suggestionsContainer.addEventListener('mousedown', this.handleSuggestionClick.bind(this)); // Use mousedown to register click before blur hides it

        // Listen for changes within the selected filters area
        this.selectedList.addEventListener('input', this.handleFilterInputChange.bind(this));
        this.selectedList.addEventListener('change', this.handleFilterInputChange.bind(this)); // Catch changes for select, etc.

        // Initialize with provided filter data if available
        this.initializeFromAttributes();
    }

    /**
     * Initialize component with data from HTML attributes.
     * Sets up initial filters and values based on provided configuration.
     * @async
     * @returns {Promise<void>}
     */
    async initializeFromAttributes() {
        // Set flag value in UI if using flags
        if (this.useFlags && this.initialState && this.initialState.flag !== undefined) {
            const flagRadios = this.querySelectorAll('input[name="softwareType"]');
            const flagValue = this.initialState.flag.toString();
            flagRadios.forEach(radio => {
                if (radio.value === flagValue) {
                    radio.checked = true;
                }
            });
        }

        // Initialize filters if we have both state and config
        if (this.initialState && this.initialState.filter && this.initialConfig) {
            try {
                // Parse filter string if it's a string
                const filterData = typeof this.initialState.filter === 'string'
                    ? JSON.parse(this.initialState.filter)
                    : this.initialState.filter;

                // Add each filter from the initial state
                for (const [filterName, filterValue] of Object.entries(filterData)) {
                    // Only add if we have configuration for this filter
                    if (this.initialConfig[filterName]) {
                        const filterConfig = this.initialConfig[filterName];
                        await this.addSelectedFilter(filterConfig);

                        // Set the value after the filter component is added and rendered
                        setTimeout(() => {
                            const filterComponent = this.selectedList.querySelector(
                                `li[data-filter-name="${filterName}"] .filter-component`
                            );
                            if (filterComponent && filterComponent.inputElement) {
                                filterComponent.inputElement.value = filterValue;
                            } else if (filterComponent && filterComponent.selectElement) {
                                filterComponent.selectElement.value = filterValue;
                            }

                            // Trigger change event to update the filter values
                            filterComponent.dispatchEvent(new Event('change', { bubbles: true }));
                        }, 0);
                    }
                }

                // Initialize currentFilterValues with the initial state
                this.currentFilterValues = {...filterData};
            } catch (e) {
                console.error('Error initializing filters from attributes:', e);
            }
        }
    }

    /**
     * Called when the element is removed from the DOM.
     * Cleans up event listeners and timers.
     */
    disconnectedCallback() {
        this.searchInput.removeEventListener('input', this.handleInput.bind(this));
        this.searchInput.removeEventListener('keydown', this.handleKeyDown.bind(this)); // Remove keydown listener
        this.searchInput.removeEventListener('blur', this.handleBlur.bind(this));
        this.suggestionsContainer.removeEventListener('mousedown', this.handleSuggestionClick.bind(this));
        this.selectedList.removeEventListener('input', this.handleFilterInputChange.bind(this));
        this.selectedList.removeEventListener('change', this.handleFilterInputChange.bind(this));

        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
        if (this.filterChangeDebounceTimer) {
             clearTimeout(this.filterChangeDebounceTimer);
        }
    }

    /**
     * Handles blur events on the search input.
     * Hides suggestions after a short delay to allow click events to register.
     */
    handleBlur() {
        // Delay hiding suggestions to allow click event to register
        setTimeout(() => {
            this.suggestionsContainer.style.display = 'none';
            this.highlightedSuggestionIndex = -1; // Reset highlight on blur
        }, 150);
    }

    /**
     * Handles keyboard navigation in the suggestion list.
     * Supports arrow keys, Enter for selection, and Escape to close.
     * @param {KeyboardEvent} event - The keyboard event
     */
    handleKeyDown(event) {
        const suggestions = this.suggestionsContainer.querySelectorAll('div[data-filter-name]');
        if (suggestions.length === 0 || this.suggestionsContainer.style.display === 'none') {
            return; // No suggestions to navigate
        }

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault(); // Prevent cursor moving in input
                this.highlightSuggestion(this.highlightedSuggestionIndex + 1);
                break;
            case 'ArrowUp':
                event.preventDefault(); // Prevent cursor moving in input
                this.highlightSuggestion(this.highlightedSuggestionIndex - 1);
                break;
            case 'Enter':
                if (this.highlightedSuggestionIndex !== -1) {
                    event.preventDefault(); // Prevent form submission or other default action
                    const selectedSuggestion = suggestions[this.highlightedSuggestionIndex];
                    if (selectedSuggestion) {
                        this.selectSuggestion(selectedSuggestion);
                    }
                }
                break;
            case 'Escape': // Optional: Hide suggestions on Escape
                 this.suggestionsContainer.style.display = 'none';
                 this.highlightedSuggestionIndex = -1;
                 break;
        }
    }

    /**
     * Highlights a suggestion at the specified index.
     * Handles wrapping around the list and scrolling to ensure visibility.
     * @param {number} index - The index to highlight
     */
    highlightSuggestion(index) {
        const suggestions = this.suggestionsContainer.querySelectorAll('div[data-filter-name]');
        const numSuggestions = suggestions.length;

        if (numSuggestions === 0) return;

        // Remove previous highlight
        if (this.highlightedSuggestionIndex !== -1 && suggestions[this.highlightedSuggestionIndex]) {
            suggestions[this.highlightedSuggestionIndex].classList.remove('highlighted');
        }

        // Clamp and wrap index
        if (index >= numSuggestions) {
            index = 0; // Wrap to top
        } else if (index < 0) {
            index = numSuggestions - 1; // Wrap to bottom
        }

        // Add new highlight
        if (suggestions[index]) {
            suggestions[index].classList.add('highlighted');
            suggestions[index].scrollIntoView({ block: 'nearest' }); // Ensure highlighted item is visible
        }

        this.highlightedSuggestionIndex = index;
    }


    /**
     * Handles input events on the search field.
     * Debounces requests and fetches suggestions after a delay.
     * @param {Event} event - The input event
     */
    handleInput(event) {
        const query = event.target.value.trim();

        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }

        if (query.length < 1) { // Minimum characters to trigger search, adjust if needed
            this.suggestionsContainer.style.display = 'none';
            this.suggestionsContainer.innerHTML = '';
            return;
        }

        this.debounceTimer = setTimeout(() => {
            this.fetchSuggestions(query);
        }, 300); // Debounce time in ms
    }

    /**
     * Fetches filter suggestions from the server based on the query.
     * @async
     * @param {string} query - The search query
     * @returns {Promise<void>}
     */
    async fetchSuggestions(query) {
        const payload = {
            flag: this.flag,
            q: query,
            // Send the current filter values as a JSON string
            filter: JSON.stringify(this.currentFilterValues)
        };
        console.log('Fetching suggestions with payload:', payload); // Log the payload for debugging

        try {
            const response = await fetch(this.endpointUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            this.displaySuggestions(data.features || {});

        } catch (error) {
            console.error('Error fetching suggestions:', error);
            this.suggestionsContainer.style.display = 'none';
            this.suggestionsContainer.innerHTML = 'Error fetching data.'; // Provide feedback
        }
    }

    /**
     * Displays the fetched suggestions in the dropdown.
     * Filters out already selected filters.
     * @param {Object} features - The features/filters to display
     */
    displaySuggestions(features) {
        this.suggestionsContainer.innerHTML = ''; // Clear previous suggestions
        this.highlightedSuggestionIndex = -1; // Reset highlight when suggestions change
        const suggestions = Object.values(features);

        suggestions.forEach(filter => {
            // Only show suggestions that haven't been selected yet
            if (!this.selectedFilters.has(filter.name)) {
                const div = document.createElement('div');
                div.textContent = filter.label;
                div.title = filter.desc ?? '';
                div._filterData = filter;
                div.dataset.filterName = filter.name;
                this.suggestionsContainer.appendChild(div);
            }
        });

        this.suggestionsContainer.style.display = suggestions.length > 0 ? 'block' : 'none';
    }

    /**
     * Handles clicks on suggestion items.
     * @param {MouseEvent} event - The mouse event
     */
    handleSuggestionClick(event) {
        const target = event.target.closest('div[data-filter-name]'); // Handle clicks inside the div too
        if (target) {
           this.selectSuggestion(target);
        }
    }

    /**
     * Handles selection of a suggestion from both click and keyboard events.
     * @param {HTMLElement} suggestionElement - The selected suggestion element
     */
    selectSuggestion(suggestionElement) {
         // Retrieve the full filter data object stored on the element
         if (suggestionElement && suggestionElement._filterData) {
            const filterData = suggestionElement._filterData; // Get the complete object

            this.addSelectedFilter(filterData); // Pass the complete object

            // Clear input and hide suggestions
            this.searchInput.value = '';
            this.suggestionsContainer.style.display = 'none';
            this.suggestionsContainer.innerHTML = '';
            this.highlightedSuggestionIndex = -1; // Reset highlight
            this.searchInput.focus(); // Keep focus on input
        }
    }

    /**
     * Adds a selected filter to the active filters list.
     * Creates the appropriate filter component based on filter type.
     * @async
     * @param {Object} filterData - The filter data object
     * @returns {Promise<void>}
     */
    async addSelectedFilter(filterData) {
        if (this.selectedFilters.has(filterData.name)) {
            return; // Avoid adding duplicates
        }

        this.selectedFilters.set(filterData.name, filterData);

        const listItem = document.createElement('li');
        listItem.dataset.filterName = filterData.name;

        let tagName = null;
        switch (filterData.class) {
            case 'Date':
            case 'Dropdown': {
                tagName = 'filter-dropdown';
                break;
            }

            case 'Language': {
                tagName = 'filter-language';
                break;
            }

            case 'Feature':
            case 'Boolean': {
                tagName = 'filter-boolean';
                break;
            }

            case 'Text':
            case 'Uri': // Treat Uri like Text for now
            default: {
                tagName = 'filter-text';
                break;
            }
        }

        // Create the remove button
        const removeButton = document.createElement('button');
        removeButton.textContent = '✕';
        removeButton.className = 'remove-btn';
        removeButton.style.cursor = 'pointer';
        removeButton.title = 'Remove filter';
        removeButton.addEventListener('click', () => {
            this.removeSelectedFilter(filterData.name);
        });
        listItem.appendChild(removeButton);

        // Create label for the filter
        const labelElement = document.createElement('label');
        labelElement.textContent = filterData.label || filterData.name;
        labelElement.htmlFor = `filter-input-${filterData.name}`;
        labelElement.title = filterData.desc ?? '';
        listItem.appendChild(labelElement);

        // Add the filter component
        await customElements.whenDefined(tagName); // Wait until defined
        const filterInputElement = document.createElement(tagName);
        filterInputElement.filterData = filterData;
        listItem.appendChild(filterInputElement);

        this.selectedList.appendChild(listItem);
    }

    /**
     * Removes a filter from the selected filters list.
     * Updates the UI and triggers a new search.
     * @param {string} filterName - The name of the filter to remove
     */
    removeSelectedFilter(filterName) {
        this.selectedFilters.delete(filterName);
        const listItem = this.selectedList.querySelector(`li[data-filter-name="${filterName}"]`);
        if (listItem) {
            listItem.remove();
        }
        // Refresh suggestions in case the user searches again for the removed filter
        this.displaySuggestions(this.getCurrentSuggestions());

        // After removing, update the filter values and trigger a new search
        this.collectFilterValues(); // Renamed method
    }

    /**
     * Helper to get current suggestions from the DOM for filtering purposes.
     * @returns {Object} Object containing current suggestion data
     */
    getCurrentSuggestions() {
        const features = {};
        // Retrieve the full data object from each suggestion div
        this.suggestionsContainer.querySelectorAll('div').forEach(div => {
            if (div._filterData) {
                features[div._filterData.name] = div._filterData;
            }
        });
        return features;
    }

    // --- Filter Input Change Handling ---

    /**
     * Handles changes to filter input values.
     * Debounces the collection of values to avoid excessive processing.
     * @param {Event} event - The input or change event
     */
    handleFilterInputChange(event) {
        // Debounce the actual processing function
        if (this.filterChangeDebounceTimer) {
            clearTimeout(this.filterChangeDebounceTimer);
        }
        this.filterChangeDebounceTimer = setTimeout(() => {
            this.collectFilterValues(); // Renamed method
        }, 400); // Debounce time in ms (adjust as needed)
    }

    /**
     * Collects values from all active filter components and dispatches a search event.
     * Gathers values from all filter components and creates a search query.
     */
    collectFilterValues() {
        // Reset the class member before collecting current values
        this.currentFilterValues = {};
        // Select all filter components directly using the added class
        const filterComponents = this.selectedList.querySelectorAll('.filter-component');

        filterComponents.forEach(filterComponent => {
            // Assume filterData and getValue exist due to FilterBase inheritance
            const filterName = filterComponent.filterData.name;
            const value = filterComponent.getValue();

            // Only include filters that have a non-empty value
            if (value !== null && value !== '') {
                this.currentFilterValues[filterName] = value;
            }
        });

        // Correctly log the class member
        console.log('Updated Filter Values:', this.currentFilterValues);

        // Dispatch the search-query event with the collected filter values
        // Make it bubble up so other components (like search-query listening on document) can catch it.
        console.log('FilterSearch: Dispatching search-query event.');
        this.dispatchEvent(new CustomEvent('search-query', {
            // Pass the raw object directly, search-query component will stringify it
            detail: {
                flag: this.flag,
                filter: JSON.stringify(this.currentFilterValues)
            },
            bubbles: true,
            composed: true // Allows crossing shadow DOM boundaries if needed later
        }));
    }

    /**
     * Helper method to set a value on a filter component.
     * @param {string} filterName - The name of the filter to set
     * @param {*} value - The value to set
     * @returns {boolean} Whether the value was successfully set
     */
    setFilterValue(filterName, value) {
        const filterComponent = this.selectedList.querySelector(
            `li[data-filter-name="${filterName}"] .filter-component`
        );

        if (!filterComponent) return false;

        // Handle different types of input elements
        if (filterComponent.inputElement) {
            filterComponent.inputElement.value = value;
            filterComponent.inputElement.dispatchEvent(new Event('input', { bubbles: true }));
            return true;
        } else if (filterComponent.selectElement) {
            filterComponent.selectElement.value = value;
            filterComponent.selectElement.dispatchEvent(new Event('change', { bubbles: true }));
            return true;
        }

        return false;
    }

}

customElements.define('matrix-search', MatrixSearch);
